import { Alert, Box, Tooltip, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';
import type { PaddleEnvironmentConfig, PaddleWebhookDiagnosticStatus } from '@modules/paddle-connect/types';
import { buildPaddleWebhookHealthView, formatPaddleHealthTime } from '@modules/paddle-connect/utils/paddleWebhookHealthView';
import { PADDLE_CONNECT_TEST_IDS } from '@modules/paddle-connect/testIds';

/** Uses the existing integration card's themed primitives; no new settings or actions. */
export function PaddleWebhookHealthStatus({ environment, config, loading, refreshFailed }: {
  environment: 'sandbox' | 'production'; config?: PaddleEnvironmentConfig;
  loading: boolean; refreshFailed: boolean;
}) {
  const view = buildPaddleWebhookHealthView(config, { loading, refreshFailed });
  return <Box data-testid={PADDLE_CONNECT_TEST_IDS.webhookStatus(environment)}
    aria-label={`${environment === 'sandbox' ? 'Sandbox' : 'Production'} webhook health`}
    role="region" aria-live="polite" aria-busy={loading} sx={{ mt: 2 }}>
    {view.notice && <Alert severity={view.rows.length ? 'warning' : 'info'} sx={{ mb: 1 }}>{view.notice}</Alert>}
    <Box component="dl" sx={{ m: 0 }}>
      {view.rows.map(row => <Box key={row.key} data-testid={PADDLE_CONNECT_TEST_IDS.webhookRow(environment, row.key)}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr)' }, gap: { xs: 0.5, sm: 2 }, py: 1 }}>
        <Typography component="dt" color="text.secondary">{row.key === 'receipt'
          ? <Tooltip describeChild title="Cumulative verified receipts since counting began; this total does not reset daily.">
            <span tabIndex={0}>{row.label}</span>
          </Tooltip> : row.label}</Typography>
        <Box component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
          <Typography sx={{ color: row.tone === 'error' ? (theme) => theme.palette.mode === 'dark'
            ? lighten(theme.palette.error.light, 0.12) : theme.palette.error.dark : row.tone === 'success'
            ? (theme) => theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark
              : 'text.primary' }}>
            {row.value}
          </Typography>
          {row.detail && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{row.detail}</Typography>}
        </Box>
      </Box>)}
    </Box>
    {view.measuredAt && !view.notice && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Measurements checked {view.measuredAt}.
    </Typography>}
  </Box>;
}

export function PaddleWebhookDiagnostic({ diagnostic }: { diagnostic?: PaddleWebhookDiagnosticStatus }) {
  if (diagnostic?.status !== 'sampled' || !diagnostic.sample) return null;
  const sampledAt = formatPaddleHealthTime(diagnostic.sample.sampledAt);
  return <Alert severity="warning" data-testid={PADDLE_CONNECT_TEST_IDS.webhookDiagnostic} sx={{ mb: 2 }}>
    Some requests could not be verified; check destination and secrets.
    <Typography variant="body2" sx={{ mt: 0.5 }}>Project diagnostic sample{sampledAt ? ` · ${sampledAt}` : ''}.
      {' '}No environment could be verified. This is separate from verified receipt totals.</Typography>
  </Alert>;
}
